import { useState, useEffect } from 'react'
import { IOption } from '../../types'
import { CUSTOM_OPTION_ID } from '../../components/forms/SearchComboBoxField'

interface FormData {
  selectedBrand: IOption | null
  fragranceName: string
  selectedImage: File | null
  existingImageUrl: string | null
  description: string
  selectedCountry: IOption | null
  year: number | null
  selectedPrice: IOption | null
  selectedPerfumer: IOption | null
  topNotes: IOption[]
  middleNotes: IOption[]
  bottomNotes: IOption[]
}

interface ValidationResult {
  isValid: boolean
  errors: Set<string>
}

const FIELD_ORDER = ['brand', 'name', 'media', 'description', 'country', 'year', 'price', 'perfumer', 'topNotes', 'middleNotes', 'bottomNotes']

const FIELD_LABELS: Record<string, string> = {
  brand: 'Brand',
  name: 'Fragrance name',
  media: 'Media',
  description: 'Description',
  country: "Where it's from",
  year: "Year it's made",
  price: 'Price',
  perfumer: 'Created by',
  topNotes: 'Top notes',
  middleNotes: 'Middle notes',
  bottomNotes: 'Base notes'
}

const isUserCreated = (option: IOption | null) => option?.id === CUSTOM_OPTION_ID

export const useFormValidation = (formData: FormData) => {
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set())

  // Auto-clear validation errors when fields are filled
  useEffect(() => {
    if (formData.selectedBrand && validationErrors.has('brand')) {
      clearError('brand')
    }
  }, [formData.selectedBrand])

  useEffect(() => {
    if (formData.fragranceName.trim() && validationErrors.has('name')) {
      clearError('name')
    }
  }, [formData.fragranceName])

  useEffect(() => {
    if (formData.description.trim() && validationErrors.has('description')) {
      clearError('description')
    }
  }, [formData.description])

  useEffect(() => {
    if (formData.selectedCountry && validationErrors.has('country')) {
      clearError('country')
    }
  }, [formData.selectedCountry])

  useEffect(() => {
    if (formData.year && validationErrors.has('year')) {
      clearError('year')
    }
  }, [formData.year])

  useEffect(() => {
    if (formData.selectedPrice && validationErrors.has('price')) {
      clearError('price')
    }
  }, [formData.selectedPrice])

  useEffect(() => {
    if (formData.selectedPerfumer && validationErrors.has('perfumer')) {
      clearError('perfumer')
    }
  }, [formData.selectedPerfumer])

  useEffect(() => {
    if ((formData.selectedImage || formData.existingImageUrl) && validationErrors.has('media')) {
      clearError('media')
    }
  }, [formData.selectedImage, formData.existingImageUrl])

  useEffect(() => {
    if (formData.topNotes.length > 0 && validationErrors.has('topNotes')) {
      clearError('topNotes')
    }
  }, [formData.topNotes])

  useEffect(() => {
    if (formData.middleNotes.length > 0 && validationErrors.has('middleNotes')) {
      clearError('middleNotes')
    }
  }, [formData.middleNotes])

  useEffect(() => {
    if (formData.bottomNotes.length > 0 && validationErrors.has('bottomNotes')) {
      clearError('bottomNotes')
    }
  }, [formData.bottomNotes])

  const clearError = (errorKey: string) => {
    setValidationErrors(prev => {
      const newErrors = new Set(prev)
      newErrors.delete(errorKey)
      return newErrors
    })
  }

  const validateForm = (): ValidationResult => {
    const errors = new Set<string>()
    const currentYear = new Date().getFullYear()

    // Brand validation (min 2 chars for custom)
    if (!formData.selectedBrand) {
      errors.add('brand')
    } else if (isUserCreated(formData.selectedBrand) && formData.selectedBrand.label.trim().length < 2) {
      errors.add('brand')
    }

    // Name validation (min 3 chars)
    if (!formData.fragranceName.trim() || formData.fragranceName.trim().length < 3) {
      errors.add('name')
    }

    // Media validation
    if (!formData.selectedImage && !formData.existingImageUrl) {
      errors.add('media')
    }

    // Description validation
    if (!formData.description.trim()) {
      errors.add('description')
    }

    // Country validation (min 2 chars for custom)
    if (!formData.selectedCountry) {
      errors.add('country')
    } else if (isUserCreated(formData.selectedCountry) && formData.selectedCountry.label.trim().length < 2) {
      errors.add('country')
    }

    // Year validation (1900 to current year)
    if (!formData.year || formData.year < 1900 || formData.year > currentYear) {
      errors.add('year')
    }

    // Price validation
    if (!formData.selectedPrice) {
      errors.add('price')
    }

    // Perfumer validation (min 3 chars for custom)
    if (!formData.selectedPerfumer) {
      errors.add('perfumer')
    } else if (isUserCreated(formData.selectedPerfumer) && formData.selectedPerfumer.label.trim().length < 3) {
      errors.add('perfumer')
    }

    // Notes validation (at least 1 note required in each category)
    if (formData.topNotes.length === 0) {
      errors.add('topNotes')
    }

    if (formData.middleNotes.length === 0) {
      errors.add('middleNotes')
    }

    if (formData.bottomNotes.length === 0) {
      errors.add('bottomNotes')
    }

    if (errors.size > 0) {
      setValidationErrors(errors)
      scrollToFirstError(errors)
    }

    return {
      isValid: errors.size === 0,
      errors
    }
  }

  const scrollToFirstError = (errors: Set<string>) => {
    const firstError = FIELD_ORDER.find(field => errors.has(field))
    if (!firstError) return

    const labelText = FIELD_LABELS[firstError]
    if (!labelText) return

    const labels = Array.from(document.querySelectorAll('label'))
    const targetLabel = labels.find(label => label.textContent === labelText)

    if (targetLabel) {
      targetLabel.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const clearAllErrors = () => {
    setValidationErrors(new Set())
  }

  return {
    validationErrors,
    validateForm,
    clearAllErrors
  }
}
